import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { X, Loader2, Eye, Download, AlertTriangle, CheckCircle2, FileStack, MinusCircle } from "lucide-react";
import { toast } from "sonner";
import { api, API } from "../lib/api";
import { openDocSlot } from "./MissingDocsCard";
import ContratPagesPicker from "./ContratPagesPicker";

async function fetchPdf(dossierId) {
  const r = await fetch(`${API}/dossiers/${dossierId}/dossier-pose/pdf`, { headers: { Authorization: `Bearer ${localStorage.getItem("norea_token")}` } });
  if (!r.ok) throw new Error((await r.json().catch(() => ({}))).detail || "Assemblage impossible");
  return r.blob();
}

function Status({ it, onGo }) {
  if (it.status === "error") return <span className="text-[11px] text-red-700 inline-flex items-center gap-1" data-testid={`pose-error-${it.key}`}><AlertTriangle className="w-3 h-3" />{it.message}</span>;
  if (it.status === "missing") return (
    <span className="text-[11px] text-amber-700 inline-flex items-center gap-1.5" data-testid={`pose-missing-${it.key}`}>
      <AlertTriangle className="w-3 h-3" />{it.message}
      <button onClick={() => onGo({ slot: it.slot_label })} className="text-norea-primary hover:underline" data-testid={`pose-deposit-${it.key}`}>Déposer →</button>
    </span>
  );
  if (it.missing_fields?.length) return (
    <span className="text-[11px] text-amber-700 inline-flex items-center gap-1.5" data-testid={`pose-fields-${it.key}`}>
      <AlertTriangle className="w-3 h-3" />{it.missing_fields.length} champ(s) vide(s)
      <button onClick={() => onGo({ tab: it.missing_fields[0].tab })} className="text-norea-primary hover:underline" title={it.missing_fields.map((m) => m.label).join(", ")} data-testid={`pose-complete-${it.key}`}>Compléter →</button>
    </span>
  );
  return <CheckCircle2 className="w-3.5 h-3.5 text-teal-600" data-testid={`pose-ok-${it.key}`} />;
}

function PieceRow({ it, index, onToggle, onChoose, onGo }) {
  const off = !it.included || it.status !== "ok";
  return (
    <div className={`flex items-center gap-3 px-3 py-2 rounded border border-transparent hover:border-norea-border transition-colors ${off ? "opacity-70" : ""}`} data-testid={`pose-row-${it.key}`}>
      <span className="w-5 text-xs tabular-nums text-norea-textMuted">{index}</span>
      <input type="checkbox" checked={it.included} onChange={(e) => onToggle(it.key, e.target.checked)} disabled={it.status !== "ok"} data-testid={`pose-include-${it.key}`} />
      <div className="flex-1 min-w-0">
        <div className="text-sm truncate">{it.label}</div>
        {it.files.length > 1 && (
          <select value={it.chosen || ""} onChange={(e) => onChoose(it.key, e.target.value)} className="mt-1 text-[11px] border border-norea-border rounded px-1.5 py-0.5 max-w-full" data-testid={`pose-file-${it.key}`}>
            {it.files.map((f) => <option key={f.key} value={f.key}>{f.filename}</option>)}
          </select>
        )}
        {it.files.length === 1 && <div className="text-[11px] text-norea-textMuted truncate">{it.files[0].filename}</div>}
      </div>
      <Status it={it} onGo={onGo} />
    </div>
  );
}

function Header({ plan, onFourni }) {
  return (
    <div className="flex flex-wrap items-center gap-x-5 gap-y-2 text-xs text-norea-textMuted mb-4" data-testid="pose-header">
      <span>Produit : <b className="text-norea-text">{plan.produit || "—"}</b></span>
      <span>Financeur : <b className="text-norea-text" data-testid="pose-financeur">{plan.financeur || "non identifié"}</b>{plan.financeur_mode && ` (ADL/mandat ${plan.financeur_mode === "generes" ? "générés" : "fournis"})`}</span>
      <span>Sous-traitant : <b className="text-norea-text">{plan.sous_traitant || "—"}</b></span>
      <label className="inline-flex items-center gap-1.5 cursor-pointer">
        <input type="checkbox" checked={plan.fourni_pose} onChange={(e) => onFourni(e.target.checked)} data-testid="pose-fourni-toggle" /> Fourni-posé
        <span className="text-[10px]">{plan.fourni_pose_custom ? "(choix du dossier)" : "(selon l'annuaire)"}</span>
      </label>
      {plan.fourni_pose_custom && <button onClick={() => onFourni(null)} className="text-norea-primary hover:underline" data-testid="pose-fourni-reset">Revenir à l'annuaire</button>}
    </div>
  );
}

export default function DossierPoseModal({ dossierId, onClose }) {
  const navigate = useNavigate();
  const [plan, setPlan] = useState(null);
  const [busy, setBusy] = useState(false);
  const load = useCallback(() => api.get(`/dossiers/${dossierId}/dossier-pose`).then((r) => setPlan(r.data)).catch(() => toast.error("Chargement impossible")), [dossierId]);
  useEffect(() => { load(); }, [load]);
  const save = (body) => api.put(`/dossiers/${dossierId}/dossier-pose`, body).then((r) => setPlan(r.data)).catch(() => toast.error("Enregistrement impossible"));
  const excluded = (plan?.items || []).filter((i) => !i.included).map((i) => i.key);
  const toggle = (key, on) => save({ excluded: on ? excluded.filter((k) => k !== key) : [...excluded, key] });
  const choose = (key, file) => save({ choices: { ...Object.fromEntries(plan.items.filter((i) => i.chosen).map((i) => [i.key, i.chosen])), [key]: file } });
  const go = ({ tab, slot }) => {
    onClose();
    if (slot) openDocSlot(slot);
    else if (tab === "configuration") navigate("/configuration");
    else window.dispatchEvent(new CustomEvent("norea:open-tab", { detail: tab }));
  };
  const run = async (download) => {
    const win = download ? null : window.open("", "_blank");
    setBusy(true);
    try {
      const url = URL.createObjectURL(await fetchPdf(dossierId));
      if (download) { const a = document.createElement("a"); a.href = url; a.download = "Dossier_de_pose.pdf"; a.click(); }
      else if (win) { win.document.title = "Dossier de pose"; win.document.body.style.margin = "0"; win.document.body.innerHTML = `<iframe src="${url}" title="Dossier de pose" style="border:0;position:fixed;inset:0;width:100vw;height:100vh"></iframe>`; }
    } catch (e) { win?.close(); toast.error(e.message); }
    finally { setBusy(false); }
  };
  const contrat = plan?.items.find((i) => i.key === "contrat_financement");
  let n = 0;
  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-6" onClick={onClose}>
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-3xl max-h-[90vh] flex flex-col border border-norea-border" onClick={(e) => e.stopPropagation()} data-testid="dossier-pose-modal">
        <div className="flex items-center justify-between px-5 py-4 border-b border-norea-border">
          <h3 className="font-heading text-lg font-medium flex items-center gap-2"><FileStack className="w-5 h-5 text-norea-primary" /> Dossier de pose</h3>
          <button onClick={onClose} className="p-1 rounded hover:bg-norea-surfaceHover" data-testid="pose-close"><X className="w-4 h-4" /></button>
        </div>
        <div className="flex-1 overflow-y-auto px-5 py-4">
          {!plan ? <div className="py-10 flex justify-center"><Loader2 className="w-5 h-5 animate-spin" /></div> : <>
            <Header plan={plan} onFourni={(v) => save({ fourni_pose: v })} />
            {[[1, "Partie 1 — documents générés"], [2, "Partie 2 — documents déposés"]].map(([part, title]) => (
              <div key={part} className="mb-4" data-testid={`pose-part-${part}`}>
                <div className="text-[11px] uppercase tracking-wider text-norea-textMuted mb-1">{title}</div>
                {plan.items.filter((i) => i.part === part).map((it) => <PieceRow key={it.key} it={it} index={++n} onToggle={toggle} onChoose={choose} onGo={go} />)}
              </div>
            ))}
            {contrat?.status === "ok" && contrat.included && <ContratPagesPicker dossierId={dossierId} item={contrat} onSaved={setPlan} />}
            {plan.skipped.length > 0 && (
              <div className="mt-4 text-xs text-norea-textMuted space-y-1" data-testid="pose-skipped">
                <div className="text-[11px] uppercase tracking-wider">Non inclus pour ce dossier</div>
                {plan.skipped.map((s) => <div key={s.key} className="flex items-center gap-1.5" data-testid={`pose-skipped-${s.key}`}><MinusCircle className="w-3 h-3" />{s.label} — {s.reason}</div>)}
              </div>
            )}
          </>}
        </div>
        <div className="px-5 py-3 border-t border-norea-border flex justify-end gap-2">
          <button onClick={() => run(true)} disabled={busy || !plan} className="btn-secondary text-sm" data-testid="pose-download"><Download className="w-4 h-4" /> Télécharger</button>
          <button onClick={() => run(false)} disabled={busy || !plan} className="btn-primary text-sm" data-testid="pose-preview">{busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Eye className="w-4 h-4" />} Aperçu du PDF assemblé</button>
        </div>
      </div>
    </div>
  );
}
